import React from 'react';
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { REQUIRED_COLUMNS, DATE_COLUMNS } from '@claimup/shared/validators/expedient-import';
import { CURRENCY_CODES } from '@claimup/shared/constants/currencies';

/**
 * Documentación de la importadora de expedientes.
 *
 * IMPORTANTE: esta pantalla es la referencia que consulta el equipo. Si se toca
 * la importadora (columnas nuevas, reglas de validación, formatos), hay que
 * actualizarla aquí y en `docs/importer-validation.md`.
 *
 * Las columnas obligatorias, las de fecha y las divisas se leen del propio
 * validador compartido (@claimup/shared/validators/expedient-import), así que esa
 * parte no se puede desincronizar del código.
 */

const Code = ({ children }) => (
  <code className='px-1 py-0.5 bg-slate-100 text-slate-700 rounded-lg text-xs whitespace-nowrap'>
    {children}
  </code>
);

const Section = ({ icon: Icon, title, children }) => (
  <section className='mb-8'>
    <div className='flex items-center mb-3'>
      <Icon className='w-5 h-5 text-slate-400 mr-2' />
      <h3 className='text-base font-semibold text-slate-800'>{title}</h3>
    </div>
    {children}
  </section>
);

// Columnas opcionales: no hay una lista única en el validador (cada una tiene su
// propia regla), así que se documentan a mano. Al añadir una, añádela aquí.
const OPTIONAL_COLUMNS = [
  ['divisa', `Divisa del importe. Vacío = EUR. Admitidas: ${CURRENCY_CODES.join(', ')}`],
  [
    'tipo_expediente',
    'account_receivable (también «gestión propia») · judicial · extra_judicial (también «recobro externo»). Vacío = el tipo por defecto de la empresa. Un valor no reconocido da error. account_receivable solo si la empresa tiene AR contratado: si no, la fila entra con el tipo de la empresa y sale un aviso',
  ],
  ['country_prefix', 'Prefijo(s) del país. Vacío = se deduce del propio número'],
  ['nif', 'NIF/CIF del deudor'],
  ['presupuesto', 'Importe del presupuesto (numérico). Solo el admin: si importa la empresa, se ignora'],
  ['porcentaje_presupuesto', 'Porcentaje del presupuesto (numérico). Solo el admin: si importa la empresa, se ignora'],
  ['operator_emails', 'Emails de operadores separados por comas. Tienen que ser de esa empresa'],
  ['notes', 'Notas internas del expediente'],
  ['nombre_comercial', 'Nombre comercial del cliente'],
  ['nombre_responsable', 'Persona de contacto del cliente'],
  ['url_pago_propia_cliente', 'URL de pago propia del cliente'],
  ['aditional_expedient_data', 'Texto libre adicional'],
  ['debt_activity_title', 'Concepto de la deuda. Vacío = se usa la referencia interna'],
  ['debt_activity_amount', 'Importe de la deuda inicial (numérico). Vacío = importe_deuda'],
  ['estado', "Estado del expediente. Vacío = 'On going'"],
];

const ImportDocsTab = () => (
  <div className='max-w-4xl'>
    <div className='mb-8'>
      <h2 className='text-lg font-semibold text-slate-800 mb-2'>
        Cómo preparar el Excel de importación
      </h2>
      <p className='text-sm text-slate-600'>
        Cada fila del Excel es un expediente. La primera fila son las cabeceras y{' '}
        <strong>el nombre de la cabecera tiene que coincidir exactamente</strong> con el de las
        tablas de abajo (en minúsculas y con guiones bajos). Las columnas que no aparezcan aquí se
        ignoran.
      </p>
    </div>

    <Section icon={ExclamationTriangleIcon} title='El archivo se valida entero antes de importar'>
      <p className='text-sm text-slate-600 mb-2'>
        Al subir el fichero se validan todas las filas. Si hay un solo error{' '}
        <strong>no se importa nada</strong>: se muestra el listado de errores por fila y columna
        para que corrijas el Excel y lo vuelvas a subir. Así no queda una importación a medias.
      </p>
      <p className='text-sm text-slate-600'>
        La misma validación se repite en el servidor fila a fila, así que tampoco se cuela nada por
        detrás.
      </p>
    </Section>

    <Section icon={InformationCircleIcon} title='Columnas obligatorias'>
      <div className='rounded-lg border border-slate-200 overflow-hidden'>
        <table className='table-auto w-full text-sm'>
          <thead className='text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200'>
            <tr>
              <th className='px-3 py-3 text-left'>Columna</th>
              <th className='px-3 py-3 text-left'>Contenido</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {REQUIRED_COLUMNS.map(([column, label]) => (
              <tr key={column}>
                <td className='px-3 py-2 align-top'>
                  <Code>{column}</Code>
                </td>
                <td className='px-3 py-2 text-slate-600'>{label}</td>
              </tr>
            ))}
            <tr>
              <td className='px-3 py-2 align-top'>
                <Code>importe_deuda</Code>
              </td>
              <td className='px-3 py-2 text-slate-600'>
                Importe de la deuda. Numérico y mayor que 0
              </td>
            </tr>
            <tr>
              <td className='px-3 py-2 align-top'>
                <Code>telefono_deudor</Code>
              </td>
              <td className='px-3 py-2 text-slate-600'>
                Al menos un teléfono (ver abajo). Entre 6 y 15 dígitos; se admite fijo
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className='mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50'>
        <p className='text-sm text-amber-900'>
          <strong>
            <Code>client_internal_reference</Code> tiene que ser única por cliente.
          </strong>{' '}
          Es la referencia con la que se decide si el expediente se crea o se actualiza: si dos
          filas del mismo cliente la repiten, la segunda no crearía un expediente nuevo, se
          sumaría a la primera. Por eso se rechaza el archivo.
        </p>
      </div>
    </Section>

    <Section icon={PhoneIcon} title='Teléfonos (uno o varios)'>
      <p className='text-sm text-slate-600 mb-3'>
        Para varios teléfonos, sepáralos por comas en la misma celda. Los prefijos van en la otra
        columna, también separados por comas y <strong>en el mismo orden</strong>: el primer
        prefijo es el del primer teléfono, el segundo el del segundo.
      </p>
      <div className='rounded-lg border border-slate-200 overflow-hidden mb-3'>
        <table className='table-auto w-full text-sm'>
          <thead className='text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200'>
            <tr>
              <th className='px-3 py-3 text-left'>country_prefix</th>
              <th className='px-3 py-3 text-left'>telefono_deudor</th>
              <th className='px-3 py-3 text-left'>Resultado</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            <tr>
              <td className='px-3 py-2'>
                <Code>+34</Code>
              </td>
              <td className='px-3 py-2'>
                <Code>600111222</Code>
              </td>
              <td className='px-3 py-2 text-slate-600'>Un teléfono español</td>
            </tr>
            <tr>
              <td className='px-3 py-2'>
                <Code>+34, +351</Code>
              </td>
              <td className='px-3 py-2'>
                <Code>600111222, 911222333</Code>
              </td>
              <td className='px-3 py-2 text-slate-600'>Uno español y uno portugués, en ese orden</td>
            </tr>
            <tr>
              <td className='px-3 py-2'>
                <Code>+34</Code>
              </td>
              <td className='px-3 py-2'>
                <Code>600111222, 611222333, 622333444</Code>
              </td>
              <td className='px-3 py-2 text-slate-600'>
                Un solo prefijo se aplica a todos los teléfonos
              </td>
            </tr>
            <tr className='bg-red-50'>
              <td className='px-3 py-2'>
                <Code>+34, +351</Code>
              </td>
              <td className='px-3 py-2'>
                <Code>600111222, 911222333, 622333444</Code>
              </td>
              <td className='px-3 py-2 text-red-700'>Error: 2 prefijos para 3 teléfonos</td>
            </tr>
          </tbody>
        </table>
      </div>
      <ul className='text-sm text-slate-600 list-disc pl-5 space-y-1'>
        <li>
          El <strong>primer teléfono es el principal</strong>: el que se usa por defecto para
          enviar SMS, WhatsApp y llamadas.
        </li>
        <li>
          El prefijo de la columna manda. Si el número ya lo trae incrustado (
          <Code>34600111222</Code> con prefijo <Code>+34</Code>) se quita solo, para no dejarlo
          duplicado.
        </li>
        <li>
          Se acepta el prefijo con <Code>+</Code> o sin él, y los repetidos se descartan.
        </li>
        <li>Máximo 20 teléfonos por deudor.</li>
      </ul>
    </Section>

    <Section icon={CalendarDaysIcon} title='Fechas'>
      <p className='text-sm text-slate-600 mb-2'>
        Todas las columnas de fecha son opcionales, pero si traen algo tiene que ser una fecha
        real. Formato <Code>dd/mm/aaaa</Code> (o una fecha de Excel de verdad).
      </p>
      <div className='flex flex-wrap gap-2'>
        {DATE_COLUMNS.map(column => (
          <Code key={column}>{column}</Code>
        ))}
      </div>
    </Section>

    <Section icon={InformationCircleIcon} title='Columnas opcionales'>
      <div className='rounded-lg border border-slate-200 overflow-hidden'>
        <table className='table-auto w-full text-sm'>
          <thead className='text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200'>
            <tr>
              <th className='px-3 py-3 text-left'>Columna</th>
              <th className='px-3 py-3 text-left'>Contenido</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {OPTIONAL_COLUMNS.map(([column, description]) => (
              <tr key={column}>
                <td className='px-3 py-2 align-top'>
                  <Code>{column}</Code>
                </td>
                <td className='px-3 py-2 text-slate-600'>{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className='text-xs text-slate-500 mt-2'>
        Pueden ir vacías, pero si traen algo tiene que ser válido: una divisa soportada, un número,
        un tipo de expediente reconocido.
      </p>
    </Section>

    <Section icon={ExclamationTriangleIcon} title='Qué pasa si la referencia ya existe'>
      <p className='text-sm text-slate-600'>
        Si ya hay un expediente de ese cliente con esa <Code>client_internal_reference</Code> (y no
        está cerrado), la fila <strong>no crea uno nuevo</strong>: se añade su deuda al expediente
        existente y se actualizan operadores y notas. Los datos del deudor (incluidos los
        teléfonos) no se tocan; para cambiarlos hay que editarlos desde el expediente.
      </p>
      <p className='text-sm text-slate-600 mt-2'>
        Solo se suma a un expediente <strong>de la misma fase</strong>: una factura AR nueva de un
        deudor cuyo expediente ya pasó a recobro abre un expediente AR nuevo.
      </p>
    </Section>

    <Section icon={InformationCircleIcon} title='Si importa la empresa'>
      <ul className='text-sm text-slate-600 list-disc pl-5 space-y-1'>
        <li>
          No hace falta la columna <Code>user_id</Code>: los expedientes quedan siempre a nombre de
          vuestra empresa.
        </li>
        <li>
          Todo entra en <strong>gestión propia</strong>, venga lo que venga en <Code>tipo_expediente</Code>. Para
          pasar un expediente a recobro está el botón «Pasar a recobro» de su ficha.
        </li>
        <li>
          <Code>presupuesto</Code> y <Code>porcentaje_presupuesto</Code> se ignoran: las condiciones
          las fija ClaimUp.
        </li>
      </ul>
    </Section>

    <Section icon={InformationCircleIcon} title='Consejos con Excel'>
      <ul className='text-sm text-slate-600 list-disc pl-5 space-y-1'>
        <li>
          Formatea como <strong>texto</strong> las columnas de teléfono y código postal: si quedan
          como número, se pierden los ceros a la izquierda.
        </li>
        <li>
          Si tu Excel usa punto y coma como separador de listas, también vale para los teléfonos y
          los prefijos.
        </li>
        <li>Solo se lee la primera hoja del libro.</li>
      </ul>
    </Section>
  </div>
);

export default ImportDocsTab;
